// Label sheet settings + layout maths for printing QR stickers.
// All sizes are in millimetres internally; the UI shows the QR size in cm.

export const MM_PER_PT = 0.3528;
export const MM_TO_PX = 96 / 25.4;

export const PAPERS = {
  A4: { label: "A4", w: 210, h: 297 },
  A5: { label: "A5", w: 148, h: 210 },
  A3: { label: "A3", w: 297, h: 420 },
  Letter: { label: "Letter", w: 215.9, h: 279.4 },
};

export const LABEL_TEMPLATES = [
  {
    id: "qr_below",
    label: "QR + text below",
    description: "QR with product, variant and SKU under it.",
  },
  {
    id: "name_top",
    label: "Name on top",
    description:
      "Product name first, then the QR, then the rest. Good for boxes.",
  },
  {
    id: "qr_side",
    label: "QR + text beside",
    description: "Wide label: QR on the left, details on the right.",
  },
  {
    id: "compact",
    label: "Compact (SKU only)",
    description: "QR with just the SKU. Smallest sticker.",
  },
  {
    id: "qr_only",
    label: "QR only",
    description: "Just the QR code, nothing else.",
  },
];

export const DEFAULT_SETTINGS = {
  template: "qr_below",
  qrSizeCm: 4,
  paper: "A4",
  orientation: "portrait",
  marginMm: 8,
  gapMm: 2,
  paddingMm: 2.5,
  columns: 0, // 0 = as many as fit
  rows: 0, // 0 = as many as fit
  copies: 1, // copies of each selected QR
  skip: 0, // skip the first N slots (partly used sticker sheet)
  pages: 0, // 0 = as many pages as needed, N = exactly N pages (repeats labels to fill)
  showProduct: true,
  showVariant: true,
  showSku: true,
  showRack: false,
  fontPt: 8,
  errorLevel: "M",
  showBorder: true,
  centerGrid: true,
};

const STORAGE_KEY = "stock_qr_label_settings_v1";

const clampNum = (v, min, max, fallback) => {
  const n = Number(v);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

// Settings are remembered in the browser so the user does not have to set them again
export const sanitizeSettings = (raw) => {
  const d = DEFAULT_SETTINGS;
  const s = { ...d, ...(raw || {}) };
  return {
    ...s,
    template: LABEL_TEMPLATES.some((t) => t.id === s.template)
      ? s.template
      : d.template,
    qrSizeCm: clampNum(s.qrSizeCm, 1.5, 12, d.qrSizeCm),
    paper: PAPERS[s.paper] ? s.paper : d.paper,
    orientation: s.orientation === "landscape" ? "landscape" : "portrait",
    marginMm: clampNum(s.marginMm, 0, 30, d.marginMm),
    gapMm: clampNum(s.gapMm, 0, 20, d.gapMm),
    paddingMm: clampNum(s.paddingMm, 0, 10, d.paddingMm),
    columns: Math.round(clampNum(s.columns, 0, 30, 0)),
    rows: Math.round(clampNum(s.rows, 0, 50, 0)),
    copies: Math.round(clampNum(s.copies, 1, 500, 1)),
    skip: Math.round(clampNum(s.skip, 0, 500, 0)),
    pages: Math.round(clampNum(s.pages, 0, 200, 0)),
    fontPt: clampNum(s.fontPt, 5, 16, d.fontPt),
    errorLevel: ["L", "M", "Q", "H"].includes(s.errorLevel)
      ? s.errorLevel
      : d.errorLevel,
    showProduct: !!s.showProduct,
    showVariant: !!s.showVariant,
    showSku: !!s.showSku,
    showRack: !!s.showRack,
    showBorder: !!s.showBorder,
    centerGrid: !!s.centerGrid,
  };
};

export const loadSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return sanitizeSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* storage full / unavailable: settings just won't persist */
  }
};

/* ---------- text on the label ---------- */

// Which text lines a label shows (depends on the template and the toggles)
export const getTextFields = (item, s) => {
  if (s.template === "qr_only") return [];
  if (s.template === "compact") {
    const text = item.sku || item.variant || item.product;
    return text ? [{ key: "sku", text, bold: true, max: 1 }] : [];
  }
  const fields = [];
  if (s.showProduct && item.product)
    fields.push({ key: "product", text: item.product, bold: true, max: 2 });
  if (s.showVariant && item.variant)
    fields.push({ key: "variant", text: item.variant, bold: false, max: 2 });
  if (s.showSku && item.sku && item.sku !== item.variant)
    fields.push({ key: "sku", text: item.sku, bold: true, max: 1 });
  if (s.showRack && item.racks)
    fields.push({
      key: "rack",
      text: `Rack: ${item.racks}`,
      bold: false,
      max: 1,
    });
  return fields;
};

const linesFor = (text, widthMm, fontPt, max) => {
  const charsPerLine = Math.max(
    4,
    Math.floor(widthMm / (fontPt * MM_PER_PT * 0.55)),
  );
  return Math.min(max, Math.max(1, Math.ceil(text.length / charsPerLine)));
};

/* ---------- label + page geometry ---------- */

export const computeCell = (items, s) => {
  const qr = s.qrSizeCm * 10;
  const pad = s.paddingMm;
  const font = s.fontPt * MM_PER_PT;
  const lh = font * 1.25;
  const side = s.template === "qr_side";
  const textW = side ? Math.max(24, qr) : qr;
  const inner = 1.2; // gap between QR and text

  let textH = 0;
  items.forEach((it) => {
    const h = getTextFields(it, s).reduce(
      (sum, f) => sum + linesFor(f.text, textW, s.fontPt, f.max) * lh,
      0,
    );
    textH = Math.max(textH, h);
  });

  let w;
  let h;
  if (side) {
    w = qr + 2 * pad + inner + textW;
    h = Math.max(qr, textH) + 2 * pad;
  } else {
    w = qr + 2 * pad;
    h = qr + 2 * pad + (textH > 0 ? textH + inner : 0);
  }
  return { qr, pad, font, lh, textW, textH, inner, w, h, side };
};

export const computeLayout = (items, s) => {
  const paper = PAPERS[s.paper];
  const landscape = s.orientation === "landscape";
  const pw = landscape ? paper.h : paper.w;
  const ph = landscape ? paper.w : paper.h;
  const cell = computeCell(items, s);

  const usableW = pw - 2 * s.marginMm;
  const usableH = ph - 2 * s.marginMm;
  const autoCols = Math.max(
    1,
    Math.floor((usableW + s.gapMm) / (cell.w + s.gapMm)),
  );
  const autoRows = Math.max(
    1,
    Math.floor((usableH + s.gapMm) / (cell.h + s.gapMm)),
  );
  const cols = s.columns > 0 ? Math.min(s.columns, autoCols) : autoCols;
  const rows = s.rows > 0 ? Math.min(s.rows, autoRows) : autoRows;

  const warnings = [];
  if (cell.w > usableW || cell.h > usableH)
    warnings.push(
      "This label is bigger than the printable area. Reduce the QR size, margin or text.",
    );
  if (s.columns > autoCols)
    warnings.push(
      `Only ${autoCols} column${autoCols !== 1 ? "s" : ""} fit across.`,
    );
  if (s.rows > autoRows)
    warnings.push(`Only ${autoRows} row${autoRows !== 1 ? "s" : ""} fit down.`);

  return {
    pw,
    ph,
    cell,
    cols,
    rows,
    perPage: cols * rows,
    autoCols,
    autoRows,
    warnings,
  };
};

// Splits the labels into pages. null = empty (skipped) slot.
export const buildPages = (items, s, layout) => {
  const seq = [];
  items.forEach((it) => {
    for (let i = 0; i < s.copies; i += 1) seq.push(it);
  });
  if (!seq.length) return { pages: [], labelCount: 0 };

  const { perPage } = layout;
  const skip = Math.min(s.skip, Math.max(0, perPage - 1));
  let labels = seq;
  if (s.pages > 0) {
    const fill = Math.max(0, s.pages * perPage - skip);
    labels = Array.from({ length: fill }, (_, i) => seq[i % seq.length]);
  }

  const slots = [...Array(skip).fill(null), ...labels];
  const pages = [];
  for (let i = 0; i < slots.length; i += perPage)
    pages.push(slots.slice(i, i + perPage));
  return { pages, labelCount: labels.length };
};
